import { useState } from 'react';
import { Check, QuestionMark, X } from '@phosphor-icons/react';
// import * as Tooltip from '@radix-ui/react-tooltip';
import clsx from 'clsx';
import { Typography } from '@la/ds-ui-components';
import { Activity } from '@la/types';
import { getLAHostnameParts } from '@la/utilities';
import {
  usePostRsvpResponseMutation,
  useUpdateRsvpResponseMutation,
} from 'redux/services/singleActivityApi';
import {
  UserRsvpResponse,
  UserSummary,
  UserSummaryRsvpResponse,
} from 'redux/services/types/singleActivity';
import {
  getInitials,
  removeDuplicates,
  transformRsvpResponse,
} from '../utils/singleActivityView.utils';
import styles from './SingleActivityViewRSVPDetails.module.scss';

type SingleActivityViewRSVPDetailsProps = {
  activity?: Activity;
  userSummaries: UserSummary[];
};

export default function SingleActivityViewRSVPDetails({
  activity,
  userSummaries,
}: Readonly<SingleActivityViewRSVPDetailsProps>) {
  const users = removeDuplicates(userSummaries || []);

  return (
    <>
      {users.length > 0
        ? users.map((user) => (
            <UserRSVP
              key={user.userId}
              activity={activity}
              userSummary={user}
            />
          ))
        : null}
    </>
  );
}

function UserRSVP({
  activity,
  userSummary,
}: Readonly<{
  activity?: Activity;
  userSummary: UserSummary;
}>) {
  const [postRsvpResponse] = usePostRsvpResponseMutation();
  const [updateRsvpResponse] = useUpdateRsvpResponseMutation();
  const { subdomain } = getLAHostnameParts();

  const { firstName, lastName } = userSummary.user;
  const response = userSummary.rsvp.response;
  const [showUpdateResponse, setShowUpdateResponse] = useState(
    response !== 'no_response'
  );
  const [rsvpResponse, setRsvpResponse] =
    useState<UserSummaryRsvpResponse>(response);

  const handleRsvpResponse = (userRsvpResponse: UserRsvpResponse) => {
    const requestBody = {
      activityId: activity?.id,
      respondentId: userSummary.userId,
      userRsvpResponse: userRsvpResponse,
      subdomain,
    };
    if (!rsvpResponse || rsvpResponse === 'no_response') {
      postRsvpResponse(requestBody)
        .unwrap()
        .then((data) => {
          setShowUpdateResponse(true);
          setRsvpResponse(transformRsvpResponse(data.response));
        });
    } else {
      updateRsvpResponse(requestBody)
        .unwrap()
        .then((data) => {
          setShowUpdateResponse(true);
          setRsvpResponse(transformRsvpResponse(data.response));
        });
    }
  };

  return (
    <div className={styles.rsvpContainer}>
      <div>
        <div className={styles.avatar}>{getInitials(firstName, lastName)}</div>
        <Typography variant="ui" size="large" weight="bold">
          {firstName} {lastName}
        </Typography>
      </div>

      <div>
        {showUpdateResponse ? (
          <>
            <button
              className={clsx({
                [styles.rsvpResponse]: true,
                [styles.rsvpResponseYes]: rsvpResponse === 'going',
                [styles.rsvpResponseNo]: rsvpResponse === 'not_going',
                [styles.rsvpResponseMaybe]: rsvpResponse === 'maybe',
              })}
              onClick={() => handleRsvpResponse('yes')}
            >
              {rsvpResponse === 'going' ? <Check size="12" /> : null}
              {rsvpResponse === 'not_going' ? <X size="12" /> : null}
              {rsvpResponse === 'maybe' ? <QuestionMark size="12" /> : null}
            </button>
            <button
              onClick={() => setShowUpdateResponse(!showUpdateResponse)}
              className={styles.responseBtn}
            >
              <Typography
                variant="ui"
                size="medium"
                className={clsx({
                  [styles.updateRsvp]: true,
                  [styles.responseYes]: rsvpResponse === 'going',
                  [styles.responseNo]: rsvpResponse === 'not_going',
                  [styles.responseMaybe]: rsvpResponse === 'maybe',
                })}
              >
                Update
              </Typography>
            </button>
          </>
        ) : (
          <>
            <button
              className={clsx({
                [styles.rsvpButton]: true,
                [styles.rsvpYes]: true,
              })}
              onClick={() => handleRsvpResponse('yes')}
            >
              <Check size="20" />
            </button>
            <button
              className={clsx({
                [styles.rsvpButton]: true,
                [styles.rsvpNo]: true,
              })}
              onClick={() => handleRsvpResponse('no')}
            >
              <X size="20" />
            </button>
            <button
              className={clsx({
                [styles.rsvpButton]: true,
                [styles.rsvpMaybe]: true,
              })}
              onClick={() => handleRsvpResponse('maybe')}
            >
              <QuestionMark size="20" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * TODO: Add tooltip to show RSVP response
 */
// function RsvpResponseToolTip({ children }: PropsWithChildren) {
//   return (
//     <Tooltip.Provider>
//       <Tooltip.Root delayDuration={0}>
//         <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
//         <Tooltip.Portal>
//           <Tooltip.TooltipContent sideOffset={5}>Going</Tooltip.TooltipContent>
//         </Tooltip.Portal>
//       </Tooltip.Root>
//     </Tooltip.Provider>
//   );
// }
